/**
 * LinkPurchaseModal — Smart match + manual search to link Amazon report orders to ML orders.
 * Also supports creating generic (non-Amazon) purchases.
 */
import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { StatusBadge } from '../../components/shared/StatusBadge';
import {
  X, Search, Loader2, Link2, AlertCircle, Check, Package,
  Plus, Truck, FileText, CreditCard, DollarSign
} from 'lucide-react';
import { useCan } from '../../lib/rbac';

// ── Link Purchase Modal (Amazon report orders → ML order) ──

interface LinkPurchaseModalProps {
  sellerId: number;
  mlOrderId: number;
  onClose: () => void;
  onLinked: () => void;
}

export function LinkPurchaseModal({ sellerId, mlOrderId, onClose, onLinked }: LinkPurchaseModalProps) {
  const canMoney = useCan()('global.view_money');
  const [tab, setTab] = useState<'smart' | 'search'>('smart');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [mlItems, setMlItems] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');

  // Load smart match candidates
  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await api.get<any>(`/amazon/link/candidates?seller_id=${sellerId}&ml_order_id=${mlOrderId}`);
      if (res.success && res.data) {
        setCandidates(res.data.candidates || []);
        setMlItems(res.data.ml_items || []);
      }
      setLoading(false);
    })();
  }, [sellerId, mlOrderId]);

  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    const res = await api.get<any[]>(`/amazon/link/search?seller_id=${sellerId}&q=${encodeURIComponent(searchQuery)}`);
    if (res.success && res.data) setSearchResults(res.data);
    setLoading(false);
  };

  const linkOrder = async (candidate: any) => {
    setLinking(true);
    setError('');

    // Auto-map: match ML items ASINs to Amazon line items
    const mappings = (candidate.line_items || []).map((li: any) => ({
      amazon_order_id: candidate.amazon_order_id,
      asin: li.asin,
      quantity: li.quantity || 1,
    }));

    if (!mappings.length) {
      setError('No line items to map');
      setLinking(false);
      return;
    }

    const res = await api.post('/amazon/link', {
      seller_id: sellerId,
      ml_order_id: mlOrderId,
      mappings,
    });

    if (res.success) {
      onLinked();
      onClose();
    } else {
      setError((res as any).error || 'Error linking');
    }
    setLinking(false);
  };

  const displayList = tab === 'smart' ? candidates : searchResults;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-[var(--color-primary)]" />
            <h3 className="font-bold text-[var(--color-text)]">Vincular Compra → Orden ML #{mlOrderId}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
        </div>

        {/* ML Order items summary */}
        {mlItems.length > 0 && (
          <div className="px-4 pt-3 pb-2 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-tertiary)] mb-1 uppercase font-medium">Items de la orden ML:</p>
            <div className="flex flex-wrap gap-2">
              {mlItems.map((item: any, i: number) => (
                <span key={i} className="px-2 py-1 bg-[var(--color-bg-tertiary)] rounded text-xs font-mono text-[var(--color-text)]">
                  {(item.seller_sku || '').slice(0, 10)} × {item.quantity || 1}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-[var(--color-border)]">
          <button onClick={() => setTab('smart')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium ${tab === 'smart' ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' : 'text-[var(--color-text-secondary)]'}`}>
            Smart Match {candidates.length > 0 && `(${candidates.length})`}
          </button>
          <button onClick={() => setTab('search')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium ${tab === 'search' ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' : 'text-[var(--color-text-secondary)]'}`}>
            Buscar Manual
          </button>
        </div>

        {/* Search bar (manual tab) */}
        {tab === 'search' && (
          <div className="flex gap-2 p-3 border-b border-[var(--color-border)]">
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Amazon Order ID, ASIN, o título..."
              className="flex-1 px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
            <button onClick={doSearch} className="px-3 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm">
              <Search size={16} />
            </button>
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
            </div>
          ) : displayList.length === 0 ? (
            <div className="text-center py-12 text-[var(--color-text-tertiary)]">
              {tab === 'smart' ? 'No se encontraron matches automáticos' : 'Busca por Order ID, ASIN, o título'}
            </div>
          ) : (
            displayList.map((candidate: any) => (
              <div key={candidate.amazon_order_id}
                className="border border-[var(--color-border)] rounded-lg p-3 hover:border-[var(--color-primary)] transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-mono text-sm font-medium text-[var(--color-text)]">{candidate.amazon_order_id}</span>
                    {candidate.match_score !== undefined && (
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                        candidate.match_score >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : candidate.match_score >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {candidate.match_score}% match
                      </span>
                    )}
                  </div>
                  <button onClick={() => linkOrder(candidate)} disabled={linking}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50">
                    {linking ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                    Vincular
                  </button>
                </div>
                <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mb-2">
                  <span>{candidate.order_date ? new Date(candidate.order_date).toLocaleDateString() : '—'}</span>
                  <span>{candidate.order_status}</span>
                  {canMoney && <span className="font-medium">${Number(candidate.net_total || 0).toFixed(2)}</span>}
                  {candidate.buyer_name && <span>{candidate.buyer_name}</span>}
                </div>
                {/* Line items */}
                <div className="space-y-1">
                  {(candidate.line_items || []).map((li: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-3 text-xs bg-[var(--color-bg-secondary)] rounded px-2 py-1">
                      <span className="font-mono text-teal-500 font-medium">{li.asin}</span>
                      <span className="text-[var(--color-text)] flex-1 truncate">{li.title}</span>
                      <span>×{li.quantity || 1}</span>
                      {canMoney && <span className="font-medium">${Number(li.unit_price || 0).toFixed(2)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Generic Purchase Modal (non-Amazon) ──

interface GenericPurchaseModalProps {
  sellerId: number;
  mlOrderId?: number;
  defaultAsin?: string;
  onClose: () => void;
  onCreated: () => void;
}

export function GenericPurchaseModal({ sellerId, mlOrderId, defaultAsin, onClose, onCreated }: GenericPurchaseModalProps) {
  const [form, setForm] = useState({
    asin: defaultAsin || '',
    quantity: 1,
    unit_cost: '',
    shipping_cost: '',
    tax_amount: '',
    supplier_name: '',
    tracking_number: '',
    carrier: '',
    invoice_number: '',
    payment_method: '',
    notes: '',
    ml_order_id: mlOrderId ? String(mlOrderId) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateField = (field: string, value: string | number) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.asin.trim() || !form.unit_cost) {
      setError('ASIN y costo unitario son obligatorios');
      return;
    }
    setSaving(true);
    setError('');

    const res = await api.post('/amazon/purchases/generic', {
      seller_id: sellerId,
      ml_order_id: form.ml_order_id ? parseInt(form.ml_order_id) : null,
      asin: form.asin,
      quantity: form.quantity,
      unit_cost: parseFloat(form.unit_cost),
      shipping_cost: parseFloat(form.shipping_cost || '0'),
      tax_amount: parseFloat(form.tax_amount || '0'),
      supplier_name: form.supplier_name || null,
      tracking_number: form.tracking_number || null,
      carrier: form.carrier || null,
      invoice_number: form.invoice_number || null,
      payment_method: form.payment_method || null,
      notes: form.notes || null,
    });

    if (res.success) {
      onCreated();
      onClose();
    } else {
      setError((res as any).error || 'Error creando compra');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Plus size={18} className="text-[var(--color-primary)]" />
            <h3 className="font-bold text-[var(--color-text)]">Nueva Compra Genérica</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          {/* Product */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1"><Package size={12} /> Producto</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-[var(--color-text-tertiary)]">ASIN *</label>
                <input type="text" value={form.asin} onChange={(e) => updateField('asin', e.target.value.toUpperCase())}
                  placeholder="B0XXXXXXXX" maxLength={12}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Cantidad</label>
                <input type="number" value={form.quantity} onChange={(e) => updateField('quantity', parseInt(e.target.value) || 1)} min={1}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
            </div>
          </fieldset>

          {/* Costs */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1"><DollarSign size={12} /> Costos</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Costo Unit. * (USD)</label>
                <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => updateField('unit_cost', e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Envío (USD)</label>
                <input type="number" step="0.01" value={form.shipping_cost} onChange={(e) => updateField('shipping_cost', e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Tax (USD)</label>
                <input type="number" step="0.01" value={form.tax_amount} onChange={(e) => updateField('tax_amount', e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
            </div>
            {form.unit_cost && (
              <div className="text-right text-sm font-medium text-[var(--color-text)]">
                Total: ${((parseFloat(form.unit_cost || '0') + parseFloat(form.shipping_cost || '0') + parseFloat(form.tax_amount || '0')) * form.quantity).toFixed(2)}
              </div>
            )}
          </fieldset>

          {/* Supplier & Tracking */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1"><Truck size={12} /> Proveedor y Envío</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Proveedor</label>
                <input type="text" value={form.supplier_name} onChange={(e) => updateField('supplier_name', e.target.value)}
                  placeholder="eBay, Walmart, etc."
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Carrier</label>
                <input type="text" value={form.carrier} onChange={(e) => updateField('carrier', e.target.value)}
                  placeholder="UPS, FedEx, USPS..."
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)]">Tracking Number</label>
              <input type="text" value={form.tracking_number} onChange={(e) => updateField('tracking_number', e.target.value)}
                placeholder="1Z999..."
                className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]" />
            </div>
          </fieldset>

          {/* Invoice */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1"><FileText size={12} /> Facturación</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Nro. Factura</label>
                <input type="text" value={form.invoice_number} onChange={(e) => updateField('invoice_number', e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Método de Pago</label>
                <select value={form.payment_method} onChange={(e) => updateField('payment_method', e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]">
                  <option value="">—</option>
                  <option value="credit_card">Tarjeta de Crédito</option>
                  <option value="debit_card">Tarjeta de Débito</option>
                  <option value="paypal">PayPal</option>
                  <option value="wire_transfer">Transferencia</option>
                  <option value="cash">Efectivo</option>
                  <option value="other">Otro</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* ML Order link */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1"><Link2 size={12} /> Vincular a Orden ML</legend>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)]">ML Order ID (opcional)</label>
              <input type="text" value={form.ml_order_id} onChange={(e) => updateField('ml_order_id', e.target.value)}
                placeholder="2000015..."
                className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]" />
              <p className="text-[10px] text-[var(--color-text-tertiary)] mt-1">Si vinculas, se calcula el profit automáticamente</p>
            </div>
          </fieldset>

          {/* Notes */}
          <div>
            <label className="text-xs text-[var(--color-text-tertiary)]">Notas</label>
            <textarea value={form.notes} onChange={(e) => updateField('notes', e.target.value)} rows={2}
              placeholder="Notas adicionales..."
              className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={saving || !form.asin.trim() || !form.unit_cost}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Crear Compra
          </button>
        </div>
      </div>
    </div>
  );
}
